/**
 * SitePick front — お問い合わせフォーム送信（docs/09 §6 / docs/10 §6）
 *
 * `.js-sp-contact` フォームを `POST /contact` に JSON 送信する。
 * 成功で `generate_lead` を push し（docs/10 §6.3）、PRG のため thanks ページへ
 * 遷移する（リロードで再送されない）。honeypot（hp_url）と署名付き時刻（t / tsig）は
 * サーバ側で検証する。Cloudflare Turnstile が有効な場合はウィジェットが挿入する
 * `cf-turnstile-response` を一緒に送り、失敗時（403 captcha_failed）はウィジェットを
 * リセットして再入力を促す。
 */

import { announce } from "./live-region.js";
import { push } from "./analytics.js";

/**
 * REST のベース URL（末尾スラッシュ付き）。
 *
 * @returns {string}
 */
function restBase() {
  const cfg = window.SITEPICK || {};
  const base = typeof cfg.restUrl === "string" && cfg.restUrl ? cfg.restUrl : "/wp-json/sitepick/v1/";
  return base.endsWith("/") ? base : `${base}/`;
}

/**
 * messages ヘルパ。
 *
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

/**
 * フィールドのエラー表示をすべて消す。
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function clearErrors(form) {
  form.querySelectorAll("[data-sp-error]").forEach((el) => {
    el.textContent = "";
    el.hidden = true;
  });
  form.querySelectorAll(".has-error").forEach((el) => el.classList.remove("has-error"));
  form.querySelectorAll("[aria-invalid=\"true\"]").forEach((el) => el.removeAttribute("aria-invalid"));
}

/**
 * サーバから返ったフィールドエラーを描画する。
 *
 * @param {HTMLFormElement} form
 * @param {Record<string, string>} errors
 * @returns {void}
 */
function showErrors(form, errors) {
  let first = null;
  Object.keys(errors || {}).forEach((name) => {
    const field = form.querySelector(`[name="${name}"]`);
    const box = form.querySelector(`[data-sp-error="${name}"]`);
    if (field) {
      field.setAttribute("aria-invalid", "true");
      const wrap = field.closest(".p-form__row");
      if (wrap) {
        wrap.classList.add("has-error");
      }
      if (!first) {
        first = field;
      }
    }
    if (box) {
      box.textContent = errors[name];
      box.hidden = false;
    }
  });
  if (first && typeof first.focus === "function") {
    first.focus();
  }
}

/**
 * Turnstile ウィジェットをリセットする（トークンは 1 回使い切りのため、再送前に必ず）。
 *
 * @returns {void}
 */
function resetTurnstile() {
  if (window.turnstile && typeof window.turnstile.reset === "function") {
    try {
      window.turnstile.reset();
    } catch {
      /* ウィジェット未描画などは無視 */
    }
  }
}

let pending = false;

/**
 * 送信ハンドラ。
 *
 * @param {SubmitEvent} event
 * @returns {Promise<void>}
 */
async function onSubmit(event) {
  event.preventDefault();
  const form = event.currentTarget;
  if (pending) {
    return;
  }

  const cfg = window.SITEPICK || {};
  const status = form.querySelector("[data-sp-form-status]");
  const submit = form.querySelector("button[type=\"submit\"]");
  const thanks = form.getAttribute("data-sp-thanks") || "";

  const payload = {
    name: (form.elements.name && form.elements.name.value) || "",
    email: (form.elements.email && form.elements.email.value) || "",
    company: (form.elements.company && form.elements.company.value) || "",
    product: (form.elements.product && form.elements.product.value) || "",
    message: (form.elements.message && form.elements.message.value) || "",
    topic: (form.elements.topic && form.elements.topic.value) || "",
    hp_url: (form.elements.hp_url && form.elements.hp_url.value) || "",
    t: (form.elements.t && form.elements.t.value) || "",
    tsig: (form.elements.tsig && form.elements.tsig.value) || "",
    turnstile_token: (form.elements["cf-turnstile-response"] && form.elements["cf-turnstile-response"].value) || "",
  };

  clearErrors(form);
  pending = true;
  if (submit) {
    submit.disabled = true;
    submit.classList.add("is-loading");
  }
  if (status) {
    status.textContent = msg("contactSending", "送信しています…");
    status.hidden = false;
  }
  announce(msg("contactSending", "送信しています…"));

  try {
    const res = await fetch(`${restBase()}contact`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": typeof cfg.nonce === "string" ? cfg.nonce : "",
      },
      body: JSON.stringify(payload),
    });

    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (res.ok && data && data.ok) {
      push("generate_lead", { topic: payload.topic || "contact" });
      if (thanks) {
        window.location.assign(thanks);
        return;
      }
      form.reset();
      if (status) {
        status.textContent = msg("contactDone", "送信しました。ご連絡ありがとうございます。");
      }
      return;
    }

    resetTurnstile();

    if (res.status === 403 && data && data.code === "captcha_failed") {
      const box = form.querySelector("[data-sp-error=\"captcha\"]");
      const captchaMsg = msg("contactCaptcha", "送信時の確認に失敗しました。少し待ってから、もう一度お試しください。");
      if (box) {
        box.textContent = captchaMsg;
        box.hidden = false;
      }
      if (status) {
        status.textContent = captchaMsg;
      }
      announce(captchaMsg);
    } else if (res.status === 429) {
      if (status) {
        status.textContent = msg("rateLimited", "アクセスが集中しています。しばらくして再度お試しください。");
      }
      announce(msg("rateLimited", "アクセスが集中しています。しばらくして再度お試しください。"));
    } else if (res.status === 422 && data && data.errors) {
      showErrors(form, data.errors);
      if (status) {
        status.textContent = msg("contactInvalid", "入力内容をご確認ください。");
      }
      announce(msg("contactInvalid", "入力内容をご確認ください。"));
    } else {
      if (status) {
        status.textContent = msg("contactError", "送信できませんでした。時間をおいて再度お試しください。");
      }
      announce(msg("contactError", "送信できませんでした。時間をおいて再度お試しください。"));
    }
  } catch {
    resetTurnstile();
    if (status) {
      status.textContent = msg("contactError", "送信できませんでした。時間をおいて再度お試しください。");
    }
    announce(msg("contactError", "送信できませんでした。時間をおいて再度お試しください。"));
  } finally {
    pending = false;
    if (submit) {
      submit.disabled = false;
      submit.classList.remove("is-loading");
    }
  }
}

/**
 * 初期化。
 *
 * @returns {void}
 */
export function initContact() {
  const form = document.querySelector(".js-sp-contact");
  if (!(form instanceof HTMLFormElement)) {
    return;
  }
  prefillAskDraft(form);
  form.addEventListener("submit", onSubmit);
}

/**
 * 相談チャットを開始できなかったときの本文（sessionStorage `sp_ask_draft`）を「お問い合わせ内容」に入れる
 * （custom-chat spec §9.2 / R9）。storage が使えない環境でも動く（try/catch）。
 *
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function prefillAskDraft(form) {
  try {
    const draft = window.sessionStorage.getItem("sp_ask_draft");
    const field = form.elements.namedItem("message");
    if (draft && field instanceof HTMLTextAreaElement && !field.value) {
      field.value = draft;
    }
    window.sessionStorage.removeItem("sp_ask_draft");
    window.sessionStorage.removeItem("sp_ask_pending");
  } catch {
    /* storage 不可でも問い合わせフォームは使える */
  }
}
